import { mergeContent, processI18N } from '@coxy/i18n-process'
import { selectedDefaultLanguageObject, selectedLanguage } from '@shared-core/features/settings'
import { useAppSelector } from '@shared-core/store/hooks'
import { type JSX, useCallback } from 'react'

type ParamValues = Record<string, string | number | undefined | JSX.Element>

type Ret<T> = {
  t: (key: keyof T[keyof T], variables?: ParamValues) => string
  language: keyof T
}

export function createI18N<T>(lang: string, defaultLang: string, ...objects: Array<T>) {
  const section = mergeContent(objects, { language: lang, fallback: defaultLang })

  return (key: keyof T[keyof T], variables?: ParamValues): string =>
    processI18N(section, {
      key,
      variables,
    })
}

export function useI18N<C>(a: C): Ret<C>
export function useI18N<C, O>(a: C, b: O): Ret<C & O>
export function useI18N<C, O, X>(a: C, b: O, c: X): Ret<C & O & X>
// eslint-disable-next-line kartoy/functions-object-params
export function useI18N<C, O, X, Y>(a: C, b: O, c: X, y: Y): Ret<C & O & X & Y>
export function useI18N<T>(...objects: Array<T>) {
  const language = useAppSelector(selectedLanguage)
  const defaultLanguageObject = useAppSelector(selectedDefaultLanguageObject)
  const i18n = createI18N(language, defaultLanguageObject, ...objects)

  const t = useCallback((key: keyof T[keyof T], variables?: ParamValues) => i18n(key, variables), [language])

  return { t, language: language }
}
